// Brevet booking — phases de cohorte auto-rollover.
//
// Pourquoi des phases ?
// La V1 hardcodait les dates de 1er cours sur les 6 slots. Conséquence : chaque
// dimanche soir, les `firstSessionDate` étaient passées, `isSlotStillBookable`
// renvoyait `false` pour tous les slots, et le funnel se cassait en prod (chips
// grisés + bouton "Préparation du paiement sécurisé…" bloqué). Bumper les dates
// chaque semaine via redéploiement n'est pas tenable.
//
// Modèle data-driven :
//  - `BREVET_BOOKING_PHASES` liste, dans l'ordre chronologique, les fenêtres de
//    booking. Chaque phase déclare quelles formules sont commercialisées, pour
//    chaque slot ID statique sa date de 1er cours (cohorte cible), ET les
//    économies d'offre (prix / heures / nb week-ends) propres à la cohorte.
//  - Le runtime choisit la première phase dont `endsAt > now`. Si aucune phase
//    n'est active, le funnel affiche un état "Sessions complètes".
//
// Économies cohorte-aware (juin 2026)
// ------------------------------------
// Les formules ne sont PLUS un prix/durée fixe : une cohorte tardive recompose
// la même formule sur moins de week-ends mais plus intensément, à un prix adapté.
// Intensité par week-end (locked spec) :
//   - Coup de pouce (decouverte) : 2h/WE   → 1 session  (slots mono)
//   - Sprint                     : 4h/WE   → 2 sessions (composites 4h)
//   - Intensif                   : 6h/WE   → 3 sessions (composite 3 sessions)
// Acompte 1€ pour tous ; reliquat = prix − 1€ (auto-prélevé après le 1er live,
// lit `HolidayCourse.price` côté backend).
//
// Règles métier en vigueur (mai-juin 2026) — mirror du backend
// `BootstrapBrevetCohortsCommand::VAGUES` :
//  - Phase 1, jusqu'au 29 mai 18h (Europe/Paris) : 3 formules (cohorte 30-31 mai).
//  - Phase 2, 29 mai 18h → 5 juin 18h : Cohorte 3, WE 6-7 juin, 3 week-ends
//    (06-07, 13-14, 20-21 juin). Les 3 formules sont vendables, recomposées sur
//    3 WE : Coup de pouce 6h→69€, Sprint 12h→149€, Intensif 18h→219€.
//  - Phase 3, 5 juin 18h → 13 juin 18h : Cohorte 4, WE 13-14 juin, 2 week-ends
//    (13-14 + 20-21 juin). Coup de pouce coupée (2 WE = trop court pour une
//    offre "découverte" séparée) ; Sprint 8h→99€, Intensif 12h→149€.
//  - Phase 4, après le 13 juin 18h : plus aucune phase active, funnel fermé.
//
// `BREVET_SLOTS` reste la définition statique (id, label, formules applicables,
// composite) ; dates + économies sont injectées au runtime via la phase active.
// Backend (StripeEventListener::findBrevetSprintCohort) consomme le slot_id +
// formula_id pour attribuer la bonne cohorte ; tant que le front pointe sur la
// bonne semaine, l'attribution suit.
import moment from "moment"
import "moment/locale/fr"

moment.locale("fr")

export const BREVET_SLOTS = [
    {
        id: "sat_10_12",
        label: "Samedi 10h-12h",
        // Coup de pouce (2h/WE) — slots mono 1 session. L'intensité réelle est
        // désormais portée par la formule (cf. economics) ; ces mono-slots
        // servent la formule "decouverte".
        applicable_formulas: ["decouverte"],
        is_composite: false,
    },
    {
        id: "sat_14_16",
        label: "Samedi 14h-16h",
        applicable_formulas: ["decouverte"],
        is_composite: false,
    },
    {
        id: "sun_10_12",
        label: "Dimanche 10h-12h",
        applicable_formulas: ["decouverte"],
        is_composite: false,
    },
    {
        // Composite 4h (2 sessions) — Sprint (4h/WE).
        id: "sat_am_pm",
        label: "Samedi 10h-12h + 14h-16h",
        applicable_formulas: ["sprint"],
        is_composite: true,
    },
    {
        id: "sat_pm_sun_am",
        label: "Samedi 14h-16h + Dimanche 10h-12h",
        applicable_formulas: ["sprint"],
        is_composite: true,
    },
    {
        id: "sat_am_sun_am",
        label: "Samedi 10h-12h + Dimanche 10h-12h",
        applicable_formulas: ["sprint"],
        is_composite: true,
    },
    {
        // Composite 6h (3 sessions) — Intensif (6h/WE). Nouveau slot.
        // FLAG: un seul variant pour le MVP multi-offre ; à confirmer/étendre par
        // le lead (ex: ajouter "Sam 14h-16h + Dim 10h-12h + Dim 14h-16h").
        id: "sat_full_sun_am",
        label: "Samedi 10h-12h + 14h-16h + Dimanche 10h-12h",
        applicable_formulas: ["intensif"],
        is_composite: true,
    },
]

// Économies d'offre par formule, mutualisées entre les phases d'une même cohorte.
// `weekends` × `hoursPerWeekend` = `totalHours`. `price` = prix total (acompte 1€
// + reliquat). Le deposit (1) et le remainder (price-1) sont dérivés au runtime.
const ECONOMICS_3_WEEKENDS = {
    decouverte: { weekends: 3, hoursPerWeekend: 2, totalHours: 6, price: 69 },
    sprint: { weekends: 3, hoursPerWeekend: 4, totalHours: 12, price: 149 },
    intensif: { weekends: 3, hoursPerWeekend: 6, totalHours: 18, price: 219 },
}
const ECONOMICS_2_WEEKENDS = {
    sprint: { weekends: 2, hoursPerWeekend: 4, totalHours: 8, price: 99 },
    intensif: { weekends: 2, hoursPerWeekend: 6, totalHours: 12, price: 149 },
}

// Économies "héritées" pour Phase 1 (cohorte 30-31 mai) : on garde la grille
// historique 4-WE pour ne pas réécrire une cohorte déjà vendue/attribuée.
const ECONOMICS_PHASE_1 = {
    decouverte: { weekends: 2, hoursPerWeekend: 2, totalHours: 4, price: 48 },
    sprint: { weekends: 4, hoursPerWeekend: 2, totalHours: 8, price: 99 },
    intensif: { weekends: 4, hoursPerWeekend: 4, totalHours: 16, price: 199 },
}

// Multi-session composites partagent la même grille horaire WE par WE.
const sprintSessions = (sat) => [
    { date: sat, time: "10:00" },
    { date: sat, time: "14:00" },
]
const intensifSessions = (sat, sun) => [
    { date: sat, time: "10:00" },
    { date: sat, time: "14:00" },
    { date: sun, time: "10:00" },
]

// Phases ordonnées dans le temps. La phase active = première dont
// `endsAt` est encore dans le futur.
// Timezone explicite Europe/Paris (offset +02:00 en juin = CEST) — éviter les
// surprises côté client si le fuseau navigateur diffère.
export const BREVET_BOOKING_PHASES = [
    {
        // Phase 1 — 3 formules dispo, cohorte WE 30-31 mai 2026 (grille 4-WE héritée).
        endsAt: "2026-05-29T18:00:00+02:00",
        allowedFormulas: ["decouverte", "sprint", "intensif"],
        formulaEconomics: ECONOMICS_PHASE_1,
        slotDates: {
            sat_10_12: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "10:00",
            },
            sat_14_16: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "14:00",
            },
            sun_10_12: {
                firstSessionDate: "2026-05-31",
                firstSessionTime: "10:00",
            },
            sat_am_pm: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "10:00",
                sessions: sprintSessions("2026-05-30"),
            },
            sat_pm_sun_am: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "14:00",
                sessions: [
                    { date: "2026-05-30", time: "14:00" },
                    { date: "2026-05-31", time: "10:00" },
                ],
            },
            sat_am_sun_am: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "10:00",
                sessions: [
                    { date: "2026-05-30", time: "10:00" },
                    { date: "2026-05-31", time: "10:00" },
                ],
            },
            sat_full_sun_am: {
                firstSessionDate: "2026-05-30",
                firstSessionTime: "10:00",
                sessions: intensifSessions("2026-05-30", "2026-05-31"),
            },
        },
    },
    {
        // Phase 2 — Cohorte 3, cohorte WE 6-7 juin 2026, 3 week-ends.
        // 3 formules recomposées sur 3 WE (06-07, 13-14, 20-21 juin).
        endsAt: "2026-06-05T18:00:00+02:00",
        allowedFormulas: ["decouverte", "sprint", "intensif"],
        formulaEconomics: ECONOMICS_3_WEEKENDS,
        slotDates: {
            sat_10_12: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "10:00",
            },
            sat_14_16: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "14:00",
            },
            sun_10_12: {
                firstSessionDate: "2026-06-07",
                firstSessionTime: "10:00",
            },
            sat_am_pm: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "10:00",
                sessions: sprintSessions("2026-06-06"),
            },
            sat_pm_sun_am: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "14:00",
                sessions: [
                    { date: "2026-06-06", time: "14:00" },
                    { date: "2026-06-07", time: "10:00" },
                ],
            },
            sat_am_sun_am: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "10:00",
                sessions: [
                    { date: "2026-06-06", time: "10:00" },
                    { date: "2026-06-07", time: "10:00" },
                ],
            },
            sat_full_sun_am: {
                firstSessionDate: "2026-06-06",
                firstSessionTime: "10:00",
                sessions: intensifSessions("2026-06-06", "2026-06-07"),
            },
        },
    },
    {
        // Phase 3 — Cohorte 4, cohorte WE 13-14 juin 2026, 2 week-ends
        // (13-14 + 20-21 juin). Coup de pouce coupée ; Sprint + Intensif seulement.
        endsAt: "2026-06-13T18:00:00+02:00",
        allowedFormulas: ["sprint", "intensif"],
        formulaEconomics: ECONOMICS_2_WEEKENDS,
        slotDates: {
            sat_am_pm: {
                firstSessionDate: "2026-06-13",
                firstSessionTime: "10:00",
                sessions: sprintSessions("2026-06-13"),
            },
            sat_pm_sun_am: {
                firstSessionDate: "2026-06-13",
                firstSessionTime: "14:00",
                sessions: [
                    { date: "2026-06-13", time: "14:00" },
                    { date: "2026-06-14", time: "10:00" },
                ],
            },
            sat_am_sun_am: {
                firstSessionDate: "2026-06-13",
                firstSessionTime: "10:00",
                sessions: [
                    { date: "2026-06-13", time: "10:00" },
                    { date: "2026-06-14", time: "10:00" },
                ],
            },
            sat_full_sun_am: {
                firstSessionDate: "2026-06-13",
                firstSessionTime: "10:00",
                sessions: intensifSessions("2026-06-13", "2026-06-14"),
            },
        },
    },
    // Phase 4 (après 2026-06-13 18h) : funnel fermé, géré par l'absence de phase active.
]

/**
 * Phase de booking active à `referenceDate` (par défaut maintenant).
 * Renvoie `null` si toutes les phases sont expirées (funnel fermé).
 */
export const getCurrentPhase = (referenceDate = null) => {
    const now = referenceDate ? moment(referenceDate) : moment()
    return (
        BREVET_BOOKING_PHASES.find((phase) => now.isBefore(moment(phase.endsAt))) || null
    )
}

/**
 * `true` quand aucune phase de booking n'est active (toutes expirées).
 * Côté UI : afficher "Sessions complètes" plutôt que le pricing.
 */
export const isFunnelClosed = (referenceDate = null) =>
    getCurrentPhase(referenceDate) === null

/**
 * Liste des formula IDs commercialisées sur la phase active.
 * Vide si funnel fermé.
 */
export const getAllowedFormulas = (referenceDate = null) => {
    const phase = getCurrentPhase(referenceDate)
    return phase ? [...phase.allowedFormulas] : []
}

/**
 * Économies d'offre cohorte-aware pour une formule sur la phase active.
 * Forme : { weekends, hoursPerWeekend, totalHours, price, deposit, remainder }.
 * `null` si funnel fermé, formule inconnue, ou non commercialisée sur la phase.
 *
 * Source de vérité du prix / des heures / du nb de week-ends affichés. Les
 * consumers (pricing, cartes, timeline) DOIVENT lire ici, pas `BREVET_FORMULAS`.
 */
export const getFormulaEconomics = (formulaId, referenceDate = null) => {
    const phase = getCurrentPhase(referenceDate)
    if (!phase || !formulaId) return null
    const econ = phase.formulaEconomics?.[formulaId]
    if (!econ) return null
    const deposit = 1
    return {
        ...econ,
        deposit,
        remainder: Math.max(0, econ.price - deposit),
    }
}

/**
 * Enrichit un slot statique avec les dates de la phase active.
 * Renvoie `null` si le slot n'existe pas, ou si la phase active ne sert pas
 * ce slot (ex: Coup de pouce en phase 3 → `sat_10_12` n'a pas de slotDates).
 */
const enrichSlotWithPhase = (slot, phase) => {
    if (!slot || !phase) return null
    const dates = phase.slotDates?.[slot.id]
    if (!dates) return null
    return { ...slot, ...dates }
}

/**
 * Un slot reste bookable tant que sa 1re session n'a pas commencé.
 *
 * Pas de buffer anticipé : l'ancienne règle "36h avant la 1re session" bloquait
 * J-1/J du WE brevet alors que la cohorte upcoming reste attribuable côté BE.
 * Ops absorbe la fenêtre courte ; un buffer plus strict reviendra avec la
 * "synchro temps réel LiveOps" V2.
 */
export const isSlotStillBookable = (slot, referenceDate = null) => {
    if (!slot || !slot.firstSessionDate || !slot.firstSessionTime) return false
    const start = moment(`${slot.firstSessionDate} ${slot.firstSessionTime}`)
    const now = referenceDate ? moment(referenceDate) : moment()
    return now.isBefore(start)
}

/**
 * Slot enrichi par son ID, contextualisé sur la phase active.
 * Renvoie `null` si pas de phase active, slot inconnu, ou slot non servi par
 * la phase. Inclut le flag `available` (cohérent avec `getBookableSlots`) pour
 * que les consumers puissent décider de basculer vers le prochain slot bookable.
 */
export const getSlotById = (id, referenceDate = null) => {
    const phase = getCurrentPhase(referenceDate)
    if (!phase) return null
    const base = BREVET_SLOTS.find((s) => s.id === id)
    const enriched = enrichSlotWithPhase(base, phase)
    if (!enriched) return null
    return { ...enriched, available: isSlotStillBookable(enriched, referenceDate) }
}

/**
 * Liste des slots de la phase active, enrichis avec dates + flag `available`.
 * Préserve l'ordre d'origine pour l'UX. Tableau vide si funnel fermé.
 */
export const getBookableSlots = (referenceDate = null) => {
    const phase = getCurrentPhase(referenceDate)
    if (!phase) return []
    const out = []
    for (const base of BREVET_SLOTS) {
        const enriched = enrichSlotWithPhase(base, phase)
        if (!enriched) continue
        out.push({
            ...enriched,
            available: isSlotStillBookable(enriched, referenceDate),
        })
    }
    return out
}

/**
 * Prochain slot bookable parmi ceux passés en argument.
 * Sert quand le slot choisi par l'utilisateur n'est plus bookable → suggérer le suivant.
 */
export const getNextBookableSlot = (slots, currentSlotId, referenceDate = null) => {
    const list = slots ?? getBookableSlots(referenceDate)
    const idx = list.findIndex((s) => s.id === currentSlotId)
    if (idx < 0) return list.find((s) => s.available) || null
    for (let i = idx + 1; i < list.length; i++) {
        if (list[i].available) return list[i]
    }
    for (let i = 0; i < idx; i++) {
        if (list[i].available) return list[i]
    }
    return null
}

/**
 * Format human pour la date du 1er cours.
 * Ex : "samedi 6 juin à 10h"
 */
export const formatFirstSessionLabel = (slot) => {
    if (!slot || !slot.firstSessionDate || !slot.firstSessionTime) return null
    const start = moment(`${slot.firstSessionDate} ${slot.firstSessionTime}`)
    return start.format("dddd D MMMM [à] HH[h]mm")
}

/**
 * Nombre de jours entre maintenant et le 1er cours.
 */
export const daysUntilFirstSession = (slot, referenceDate = null) => {
    if (!slot || !slot.firstSessionDate || !slot.firstSessionTime) return null
    const start = moment(`${slot.firstSessionDate} ${slot.firstSessionTime}`)
    const now = referenceDate ? moment(referenceDate) : moment()
    return Math.max(start.diff(now, "days"), 0)
}
